/**
 * fileDropRouter — side-effecting routing for dropped files (toasts, opening
 * tabs, analytics), factored out of `useFileDrop` so:
 *  a) the DOM handler stays a thin adapter (resolve `{name, path}` entries via
 *     `getFilePath`, delegate here);
 *  b) e2e tests can drive the SAME routing without a synthetic drag/drop event.
 *     Electron's contextBridge does not let page scripts reassign
 *     `window.electronAPI.getPathForFile` (confirmed empirically — the
 *     override silently no-ops), so faking a native file path for a synthetic
 *     `DataTransfer`/`File` drop is unreliable. `routeDroppedFiles` is exposed
 *     on `window.__TEST_STORES__` (see `sessions/index.ts`) so tests can pass
 *     real absolute fixture paths directly and exercise the exact same
 *     `decideDropAction` → open-tab code path.
 */
import { openScriptInTab } from '../script-browser/services/scriptBrowserApi';
import { openDataImportTab } from '../workspace/data-import/openDataImportTab';
import { toast } from '../../components/ui/toaster';
import { decideDropAction } from './fileDropClassifier';
import { isBetaFeatureEnabled } from '../../core/beta';

export type DroppedFileEntry = { name: string; path: string };
export type RegisteredFileDropTarget = {
  label: string;
  onDrop: (entries: DroppedFileEntry[]) => void | Promise<void>;
};

const fileDropTargets = new Map<HTMLElement, RegisteredFileDropTarget>();

export function registerFileDropTarget(element: HTMLElement, target: RegisteredFileDropTarget): () => void {
  fileDropTargets.set(element, target);
  return () => {
    if (fileDropTargets.get(element) === target) fileDropTargets.delete(element);
  };
}

export function fileDropTargetAt(eventTarget: EventTarget | null): RegisteredFileDropTarget | undefined {
  let element = eventTarget instanceof HTMLElement ? eventTarget : null;
  while (element) {
    const target = fileDropTargets.get(element);
    if (target) return target;
    element = element.parentElement;
  }
  return undefined;
}

async function openSqlEntries(entries: DroppedFileEntry[]): Promise<void> {
  for (const entry of entries) {
    if (!entry.path) {
      toast.error({ title: 'Could not read file path', description: entry.name });
      continue;
    }

    try {
      const result = await openScriptInTab(entry.path);
      if (result === 'file-not-found') {
        toast.error({ title: 'File not found', description: entry.path });
      } else if (result === null) {
        toast.error({ title: 'Failed to open file', description: entry.path });
      }
    } catch (err) {
      console.error('[FileDrop] Error opening file:', entry.path, err);
      toast.error({ title: 'Error opening file', description: entry.path });
    }
  }
}

function openImportEntry(entry: DroppedFileEntry, ignoredCount: number): void {
  if (!entry.path) {
    toast.error({ title: 'Could not read file path', description: entry.name });
    return;
  }

  openDataImportTab({ filePath: entry.path });
  // No filename/PII in analytics props — the filename in the toast below is fine.
  window.electronAPI?.analytics
    ?.track('feature:data_import_open', { source: 'file_drop' })
    .catch(() => {});

  if (ignoredCount > 0) {
    toast.warning({
      title: `Importing ${entry.name}`,
      description: `${ignoredCount} other file(s) were ignored — drop one file at a time to import several.`,
    });
  }
}

/** Routes dropped files by name via `decideDropAction` — an import file wins
 *  (first one only, rest ignored), else all `.sql` files open, else an
 *  unsupported-file-type toast lists every skipped name. */
export async function routeDroppedFiles(entries: DroppedFileEntry[]): Promise<void> {
  if (entries.length === 0) return;

  const { action, ignoredCount } = decideDropAction(entries.map((e) => e.name));

  if (action.kind === 'import') {
    // Data Import is opt-in (Settings → Beta Features). Say so rather than
    // falling through to the generic unsupported-type toast, whose list of
    // supported extensions would still name .csv / .xlsx.
    if (!isBetaFeatureEnabled('data-import')) {
      toast.warning({
        title: 'Data Import is turned off',
        description: 'Turn it on under Settings → Beta Features to import data files.',
      });
      return;
    }
    openImportEntry(entries[action.index], ignoredCount);
    return;
  }

  if (action.kind === 'sql') {
    await openSqlEntries(action.indices.map((i) => entries[i]));
    if (action.unsupportedNames.length > 0) {
      toast.warning({
        title: 'Unsupported file type',
        description: `Only .sql files are supported. Skipped: ${action.unsupportedNames.join(', ')}`,
      });
    }
    return;
  }

  toast.warning({
    title: 'Unsupported file type',
    description: `Supported: .sql, .csv, .tsv, .txt, .json, .ndjson, .xlsx. Skipped: ${entries
      .map((e) => e.name)
      .join(', ')}`,
  });
}
